import { ShareNetworkIcon as ShareIcon } from "@wags/ui/product-icons.tsx";

import { FavoriteButtonFallback } from "@/features/saved-listings";
import { useProfilePageDataEnabled } from "./profile-page-flag";
/* eslint-disable react-doctor/no-giant-component, react-doctor/no-multi-comp */

import {
    ClientOnly,
    Link,
    getRouteApi,
    useNavigate,
    useRouterState,
} from "@tanstack/react-router";
import type { ListingPage } from "@wags/catalog/listings/listing-page.contract.ts";
import { cn } from "@wags/ui/ui-utils";
import {
    getDogSexAvailability,
    getListingProviderType,
    listingContactPreview,
    pickPreferredImageMedia,
} from "@wags/catalog/pure.ts";
import { usePostHog } from "posthog-js/react";
import { lazy, type ReactNode, Suspense, useRef, useState } from "react";
import { useElementAboveViewportTop } from "@wags/tanstack-start/browser";
import { KitsCopilotPageContext } from "@/features/kits-conversations";
import { ListingProviderName } from "./listing-provider-name";
import { DogActionRow } from "./dog-action-row";
import { ListingContactHelp } from "./listing-contact-help";
import { useListingContactHelp } from "./use-listing-contact-help";
import { AdoptedDogSummary } from "./adopted-dog-summary";
import { KitsPillHold } from "./kits-pill-hold";
import { useScrollPlaced } from "./use-scroll-placed";
import { useDogContact } from "./use-dog-contact";
import { buildListingJsonLd } from "./dog-json-ld";
import {
    DesktopBackButton,
    DesktopBuyBox,
    getBuyBoxVariant,
} from "./dog-desktop-buy-box";
import { ListingCarousel, ListingRecommendations } from "./dog-lazy-sections";
import { Header } from "@/components/Header";
import { ReadMoreExpandable } from "@wags/ui/read-more-expandable.tsx";
import { resolveMaxDistanceMiles } from "@wags/discovery/pure.ts";
import { iconButtonClass } from "@wags/ui/icon-button-class.ts";
import { BrowserEdges } from "@wags/ui/browser-edges.tsx";
import { EDGE_COLORS } from "@wags/ui/edge-color.ts";
import { StickyMobileHeader } from "@wags/ui/sticky-mobile-header.tsx";
import { UnpricedListingSummary } from "./unpriced-listing-summary";
import { DogDesktopHeader } from "./dog-desktop-header";
import { dogCopy } from "./dog-route-data";
import type { DogPageSectionsProps } from "./dog-page-sections";
import { useMoreDogs } from "./more-dogs/use-more-dogs";
import {
    type DogGallery,
    DogMobileGallery,
    DogMobileHero,
} from "./dog-mobile-hero";
import { shareOfferDogName, shareOfferText } from "./share-offer";
import { useShareOfferAfterSave } from "./use-share-offer-after-save";
import {
    DOG_CONTENT_COLUMN,
    DOG_CONTENT_COLUMN_LEFT,
    DOG_CONTENT_COLUMN_RIGHT,
    DOG_DESKTOP_GRID,
} from "./dog-desktop-layout";
import {
    FloatingListingAction,
    HeaderListingAction,
} from "./dog-listing-actions";
import { DogListingDetails } from "./dog-listing-details";
import {
    type DogPageMode,
    contactListingState,
    missingListingDetail,
    dockPresentation,
    dogListingPresentation,
    providerKindOf,
    type ListingActionArea,
} from "./dog-listing-presentation";
import { FavoriteButton } from "./lazy-favorite-button";
import { useAuth } from "@/lib/core/auth-protocol";
import { useBackNavigation } from "@wags/tanstack-start/browser";
import { useChatFabContentDock } from "@/features/kits-conversations";
import { useListingSectionData } from "./use-listing-section-data";
import { useKitsCopilotEnabled } from "@/features/kits-conversations";
import { usePublishViewState } from "@/features/kits-conversations";
import {
    StatusSticker,
    compatibilityInputFromWagsListing,
    collectListingPhotoItems,
    DogFullBleedMediaPanel,
    DogListingPhotoWell,
    resolveDogPhotoWellState,
    ListingDescriptionMarkdown,
    mergeListingPage,
    type CollectionWagsListing,
    formatBreedNames,
    useListingPageV2,
    VitalsSection,
    type ShareSource,
    shareListingWithTracking,
} from "@/features/listings";
import { useRecordListingView } from "@/features/saved-listings";
import { TextKitsExplainerAnchor } from "@/features/text-kits-explainer";
import { useUserLocation } from "@/features/viewer-location";
import { useSeekerState } from "@/features/viewer-location";
import {
    mapOrgReviewToReview,
    type WagsOrgReview,
    type Fact,
    usePrefetchProviders,
    useProviderProfileV2,
    useWagsOrganizationsReviewsLiveQuery,
} from "@/features/providers";
import {
    idleEnabled,
    idleOrganizationId,
    idleProviderIds,
    useAfterIdle,
    useDesktopLayout,
} from "./use-after-idle";
import { formatMoneyRange } from "@/lib/ui/price";
import { dogListingOgImageUrl } from "./dog-share-media";

type ListingApplication = NonNullable<
    NonNullable<ReturnType<typeof useListingPageV2>["data"]>["application"]
>;

function mergeScreeningFacts(
    application: ListingApplication | undefined,
    providerOperationsFacts: Fact[] | undefined,
): Fact[] | undefined {
    const listingFacts = (application?.screening ?? []).map((fact) => ({
        key: fact.key,
        label: fact.label,
        value: fact.value,
    }));
    const seen = new Set(listingFacts.map((fact) => fact.key));
    const providerExtras = (providerOperationsFacts ?? []).filter(
        (fact) => !seen.has(fact.key),
    );
    const merged = [...listingFacts, ...providerExtras];
    return merged.length > 0 ? merged : undefined;
}

const FAVORITE_ICON_BUTTON_FALLBACK = <FavoriteButtonFallback variant="icon" />;
const ListingClientSetup = lazy(() =>
    import("./listing-client-setup").then((module) => ({
        default: module.ListingClientSetup,
    })),
);
type DogListing = CollectionWagsListing;

function getDogRouteBasics(dog: DogListing | undefined) {
    return {
        organizationId: dog?.organizationId ?? "",
        providerOrg: dog?.organization,
        providerType: getListingProviderType(
            dog?.providerType,
            dog?.organization?.type,
        ),
        publishedName: dog?.title?.trim().slice(0, 80) || undefined,
    };
}

function deriveDogListingData(
    dog: DogListing,
    priceRange: DogListing["priceRange"],
    listingPageImages: Array<{ url: string }>,
) {
    const sexAvailability = getDogSexAvailability(
        dog.numberOfMalesAvailable,
        dog.numberOfFemalesAvailable,
    );
    const mediaItems = collectListingPhotoItems({
        media: dog.media,
        imageUrls: dog.imageUrls,
        listingPageImages,
    });

    const org = dog.organization;
    const breederName = org?.name?.trim();
    const priceDisplay = formatMoneyRange(priceRange) ?? null;
    const breedNamesDisplay = formatBreedNames(dog);
    const breedSlugs = dog.breeds.map((b) => b.slug);
    const { dogName, dogDescription } = dogCopy(dog);

    return {
        sexAvailability,
        mediaItems,
        breederName,
        priceDisplay,
        breedNamesDisplay,
        breedSlugs,
        dogName,
        dogDescription,
        parentDogs: dog.parentDogs,
        providerIsVerified: Boolean(org?.isVerified),
    };
}

const DOG_PAGE_LABEL: Record<DogPageMode, ReactNode> = {
    listing: null,
    draft: (
        <div className="mb-4 text-center md:text-left">
            <span className="inline-flex rounded-label bg-havanese px-3 py-1 text-body-small font-semibold text-doberman-black">
                Draft listing
            </span>
        </div>
    ),
};

const listingRouteApi = getRouteApi("/_find/dog/$dogId");
const draftRouteApi = getRouteApi("/dog/$dogId_/draft");

export function dogViewStateSlice(
    listingId: string,
    publishedName: string | undefined,
) {
    return {
        listingId,
        ...(publishedName === undefined ? {} : { name: publishedName }),
    };
}

export function ListingRouteComponent() {
    const { dogId } = listingRouteApi.useParams();
    const dog = listingRouteApi.useLoaderData();
    return <DogPage dogId={dogId} dog={dog} mode="listing" />;
}

export function DraftRouteComponent() {
    const { dogId } = draftRouteApi.useParams();
    const dog = draftRouteApi.useLoaderData();
    return (
        <DogPage
            dogId={dogId}
            dog={dog}
            mode="draft"
            draftPage={dog.draftPage}
        />
    );
}

function reviewsForListing(
    reviewsData: { total: number; reviews: WagsOrgReview[] } | undefined,
) {
    if (!reviewsData?.reviews.length) return null;
    return {
        total: reviewsData.total,
        reviews: reviewsData.reviews.map((review) =>
            mapOrgReviewToReview(review),
        ),
    };
}

function enabledOnPublicListing(mode: DogPageMode, enabled: boolean) {
    return mode === "listing" && enabled;
}

type DogPageProps = {
    dogId: string;
    dog: DogListing;
} & (
    | { mode: "listing"; draftPage?: never }
    | { mode: "draft"; draftPage: ListingPage }
);

function dogPageListingPage(
    props: DogPageProps,
    publicListingPage: ListingPage | null,
) {
    return props.mode === "draft" ? props.draftPage : publicListingPage;
}

function galleryMounted(idleNow: boolean, index: number) {
    return idleNow || index !== 0;
}

function desktopPhotoWell(
    dogId: string,
    { state, ...gallery }: DogGallery,
    onOpenFullscreen: (index: number) => void,
) {
    return (
        <DogListingPhotoWell state={state}>
            <DogFullBleedMediaPanel
                key={dogId}
                scrollRestorationId={`listing-gallery-${dogId}-desktop`}
                {...gallery}
                onOpenFullscreen={onOpenFullscreen}
                priority
            />
        </DogListingPhotoWell>
    );
}

function factsCardReplacesVitals(
    action: ListingActionArea,
    arm: string | null,
) {
    return action.kind === "buy" && action.contactable && arm === "facts";
}

function DogPage(props: DogPageProps) {
    const { dogId, dog, mode } = props;
    const navigate = useNavigate();
    const handleBack = useBackNavigation();
    const { location: userLocation } = useUserLocation();
    const posthog = usePostHog();
    const { isSignedIn } = useAuth();

    const { organizationId, providerOrg, providerType, publishedName } =
        getDogRouteBasics(dog);
    const afterIdle = useAfterIdle();
    const showDesktopPhoto = useDesktopLayout();
    const [carouselMounted, setCarouselMounted] = useState(false);
    usePrefetchProviders(idleProviderIds(afterIdle, organizationId));
    const { data: reviewsData } = useWagsOrganizationsReviewsLiveQuery(
        idleOrganizationId(afterIdle, organizationId),
        { limit: 5 },
    );

    const profilePageDataEnabled = useProfilePageDataEnabled();
    const deferredProfileData = idleEnabled(afterIdle, profilePageDataEnabled);
    const providerProfile = useProviderProfileV2(
        organizationId,
        providerType,
        deferredProfileData,
    );
    const providerAvatarUrl =
        pickPreferredImageMedia(providerOrg?.media ?? [], null)?.url ||
        undefined;

    const { data: publicListingPage, isPending: listingPagePending } =
        useListingPageV2(
            dogId,
            enabledOnPublicListing(mode, deferredProfileData),
        );
    const listingPageV2 = dogPageListingPage(props, publicListingPage);

    const screeningFacts = mergeScreeningFacts(
        listingPageV2?.application ?? undefined,
        providerProfile?.factGroups.find(
            (group) => group.category === "operations",
        )?.facts,
    );

    const [isPhotoCarouselOpen, setIsPhotoCarouselOpen] = useState(false);
    const mobileHeaderRef = useRef<HTMLDivElement>(null);
    const contentSheetRef = useRef<HTMLDivElement>(null);
    const [mediaSelection, setMediaSelection] = useState({ dogId, index: 0 });
    const selectedMediaIndex =
        mediaSelection.dogId === dogId ? mediaSelection.index : 0;
    const setSelectedMediaIndex = (index: number) =>
        setMediaSelection({ dogId, index });
    const openPhoto = (index: number) => {
        setCarouselMounted(true);
        setSelectedMediaIndex(index);
        setIsPhotoCarouselOpen(true);
    };
    const kitsCopilotEnabled = useKitsCopilotEnabled();
    const copilotEnabled = enabledOnPublicListing(mode, kitsCopilotEnabled);

    const dogViewStatePublication = usePublishViewState(
        "dog",
        dogViewStateSlice(dogId, publishedName),
        { leadingEdge: true },
    );

    const { data: batchListing } = useListingSectionData(dog);

    const seekerState = useSeekerState();
    const listing = mergeListingPage(batchListing, listingPageV2, seekerState);

    const trackedDogRef = useRef<string | null>(null);
    const recordView = useRecordListingView();

    const [headerSentinel, setHeaderSentinel] = useState<HTMLDivElement | null>(
        null,
    );
    const scrolledPastHeader = useElementAboveViewportTop(headerSentinel);

    const buyBoxParam = useRouterState({
        select: (state) =>
            (state.location.search as { buybox?: string }).buybox,
    });
    const buyBoxVariant = getBuyBoxVariant(buyBoxParam);

    const priceRange = listingPageV2?.listing.price ?? dog.priceRange;
    const listingState = contactListingState(dog.status, priceRange);

    const recordDogView = (node: HTMLDivElement | null) => {
        if (
            !node ||
            !dog ||
            dog.detailHydratedAt == null ||
            trackedDogRef.current === dogId
        )
            return;
        trackedDogRef.current = dogId;

        if (mode === "listing") {
            posthog.capture("listing_viewed", {
                dogId,
                dogName: dog.title?.trim() || "Puppy",
                breed: formatBreedNames(dog),
                providerType,
                source: dog.organization?.source,
                listing_state: listingState,
            });
            void recordView({
                listingId: dogId,
                source: "listing_detail",
            }).catch(() => {});
        }
    };

    const {
        sexAvailability,
        mediaItems,
        breederName,
        priceDisplay,
        breedNamesDisplay,
        breedSlugs,
        dogName,
        dogDescription,
        parentDogs,
        providerIsVerified,
    } = deriveDogListingData(
        dog,
        priceRange,
        listingPageV2?.listing.images ?? [],
    );
    const contactPreview = listingContactPreview(dog, {
        type: providerType,
        name: breederName,
        isVerified: providerIsVerified,
    });
    const photoWellState = resolveDogPhotoWellState({
        photoCount: mediaItems.length,
        listingPagePending: enabledOnPublicListing(mode, listingPagePending),
    });
    const listingJsonLd = buildListingJsonLd({
        mode,
        dog,
        dogId,
        dogName,
        breedNamesDisplay,
        mediaItems,
        breederName,
        priceRange,
    });

    const orgCoords = dog.organization?.location;
    const organizationLocationLabel = [
        dog.organization?.city,
        dog.organization?.state,
    ]
        .filter(Boolean)
        .join(", ");

    const [actionRowSentinel, setActionRowSentinel] =
        useState<HTMLSpanElement | null>(null);
    const [actionRow, setActionRow] = useState<HTMLDivElement | null>(null);
    const scrollPlaced = useScrollPlaced();
    const actionRowOutOfView = useElementAboveViewportTop(actionRowSentinel, {
        enabled: scrollPlaced,
    });
    const presentation = dogListingPresentation({
        status: dog.status,
        mode,
        photoWellState,
        priceDisplay,
        dog,
    });
    const { action, showAdoptedSummary: adoptedSummary } = presentation;
    useChatFabContentDock(!adoptedSummary);

    const dock = dockPresentation(action, {
        copilotEnabled,
        actionRowOutOfView,
    });
    const heroSticker = presentation.sticker;
    const vitalsListing = (() =>
        presentation.showListingDetails ? listing : null)();
    const kitsPillHold = (() =>
        copilotEnabled && <KitsPillHold key={dogId} actionRow={actionRow} />)();

    const listingReviews = reviewsForListing(reviewsData);
    const moreDogs = useMoreDogs({
        dog,
        listing,
        dogName,
        listingMode: mode === "listing",
        afterIdle,
        providerName: breederName,
        providerAvatarUrl,
        providerKind: providerKindOf(providerType),
        reviews: listingReviews,
    });
    const sectionProps: DogPageSectionsProps = {
        listing,
        dogId,
        dogName,
        copilotEnabled,
        providerType,
        providerAvatarUrl,
        providerProfile,
        listingPageV2,
        parentDogs,
        sexAvailability,
        reviews: listingReviews,
        organizationId: dog.organizationId,
        organizationLocationLabel,
        screeningFacts,
        providerCard: moreDogs.providerCard,
    };
    const contactHelp = useListingContactHelp({
        sections: sectionProps,
        providerName: breederName,
        eligible: presentation.headerAction === "cta",
    });
    const wrapContact = (surface: "desktop" | "mobile") => (cta: ReactNode) => (
        <ListingContactHelp details={contactHelp} surface={surface}>
            {cta}
        </ListingContactHelp>
    );

    const gallery: DogGallery = {
        state: photoWellState,
        mediaItems,
        alt: dogName,
        index: selectedMediaIndex,
        onIndexChange: setSelectedMediaIndex,
        active: !isPhotoCarouselOpen,
        mountGallery: galleryMounted(afterIdle, selectedMediaIndex),
        endSlide: moreDogs.endSlide,
    };

    const { transaction, primaryAction, inlineBuy, statusContact } =
        useDogContact({
            dogId,
            dogName,
            compatibility: compatibilityInputFromWagsListing(dog),
            providerType,
            contactPreview,
            action,
            dock,
            priceRange,
            listingState,
            copilotEnabled,
            ask: contactHelp.ask,
        });
    const hideVitals = factsCardReplacesVitals(
        action,
        contactHelp.experiment.arm,
    );
    const buyBoxPrice =
        action.kind === "buy" && action.price !== null ? action.price : null;
    const shareDog = (source: ShareSource) =>
        shareListingWithTracking({
            posthog,
            source,
            dogId,
            dogName,
            title: `${dogName} - ${breedNamesDisplay}`,
            draft: mode === "draft",
        });
    const shareOffer = useShareOfferAfterSave({
        posthog,
        isSignedIn: Boolean(isSignedIn),
        listingId: dogId,
        text: shareOfferText(shareOfferDogName(dog)),
        photoUrl: dogListingOgImageUrl(mediaItems),
        providerKind: providerKindOf(providerType),
        onShare: () => shareDog("after-save-prompt"),
    });

    const saveShareActions = (
        <>
            {dock.iconSave && (
                <ClientOnly fallback={FAVORITE_ICON_BUTTON_FALLBACK}>
                    <Suspense fallback={FAVORITE_ICON_BUTTON_FALLBACK}>
                        <FavoriteButton
                            listingId={dogId}
                            variant="icon"
                            onSaved={shareOffer.handleSaved}
                            onUnsaved={shareOffer.handleUnsaved}
                        />
                    </Suspense>
                </ClientOnly>
            )}
            <button
                type="button"
                aria-label="Share this listing"
                className={iconButtonClass}
                onClick={() => void shareDog("dog-page")}
            >
                <ShareIcon className="size-6" />
            </button>
        </>
    );
    const breadcrumbItems = [
        { label: "Find", to: "/find" as const },
        {
            label: breedNamesDisplay,
            onClick: () => {
                const lat = userLocation?.lat ?? orgCoords?.y;
                const lng = userLocation?.lon ?? orgCoords?.x;
                if (lat != null && lng != null) {
                    navigate({
                        to: "/find",
                        search: {
                            lat,
                            lng,
                            breedSlugs,
                            maxDistanceMiles: resolveMaxDistanceMiles(),
                            page: 0,
                            limit: 50,
                        },
                    });
                } else {
                    navigate({ to: "/find" });
                }
            },
        },
        { label: dogName },
    ];

    const headerHandoffSentinel = (
        <div
            ref={setHeaderSentinel}
            aria-hidden="true"
            className="pointer-events-none relative -top-[var(--dog-header-height)] hidden dog-desktop:block"
        />
    );

    const renderPage = () => (
        <div
            ref={recordDogView}
            className="min-h-dvh w-full flex flex-col bg-true-white pb-[calc(env(safe-area-inset-bottom)+7rem)] dog-desktop:pb-0"
        >
            {(() => (
                <>
                    {mode === "listing" && (
                        <>
                            <span
                                ref={dogViewStatePublication.updateRef}
                                hidden
                            />
                            <span
                                ref={dogViewStatePublication.ownerRef}
                                hidden
                            />
                        </>
                    )}
                    {!adoptedSummary && (
                        <style>{`:root { --kits-dock-left: ${DOG_CONTENT_COLUMN_LEFT}; --kits-dock-right: ${DOG_CONTENT_COLUMN_RIGHT}; }`}</style>
                    )}
                    {mode === "listing" && (
                        <ClientOnly fallback={null}>
                            <Suspense fallback={null}>
                                <ListingClientSetup listing={dog} />
                            </Suspense>
                        </ClientOnly>
                    )}
                    {listingJsonLd && (
                        <script type="application/ld+json">
                            {listingJsonLd}
                        </script>
                    )}
                    <StickyMobileHeader
                        ref={mobileHeaderRef}
                        onBack={handleBack}
                        className="md:flex dog-desktop:hidden"
                    >
                        <div className="flex items-center gap-2">
                            {saveShareActions}
                        </div>
                    </StickyMobileHeader>
                    <BrowserEdges
                        initial={{
                            top: EDGE_COLORS.trueWhite,
                            bottom: EDGE_COLORS.trueWhite,
                        }}
                    />

                    {showDesktopPhoto ? (
                        <DogDesktopHeader
                            breadcrumbItems={breadcrumbItems}
                            hideBreadcrumb={adoptedSummary}
                            navigation={
                                <Header
                                    className="static bg-true-white p-0 md:p-0"
                                    hideScrollBorder
                                    leading={
                                        <DesktopBackButton
                                            onClick={handleBack}
                                        />
                                    }
                                />
                            }
                            showSummary={scrolledPastHeader}
                            title={
                                <span className="font-family-heading">
                                    {dogName}
                                </span>
                            }
                        >
                            <HeaderListingAction
                                action={presentation.headerAction}
                                priceDisplay={priceDisplay}
                                primaryAction={primaryAction}
                                contact={statusContact}
                                draftActions={saveShareActions}
                            />
                        </DogDesktopHeader>
                    ) : null}

                    {!adoptedSummary && (
                        <DogMobileHero
                            sticker={
                                heroSticker ? (
                                    <StatusSticker
                                        id={dogId}
                                        status={heroSticker}
                                        size="hero"
                                    />
                                ) : null
                            }
                            aboveThumbnails={shareOffer.message}
                            aboveThumbnailsRef={shareOffer.slotRef}
                        >
                            {!showDesktopPhoto && (
                                <DogMobileGallery
                                    dogId={dogId}
                                    gallery={gallery}
                                    priority
                                    coveredBy={[
                                        mobileHeaderRef,
                                        contentSheetRef,
                                    ]}
                                />
                            )}
                            <TextKitsExplainerAnchor
                                listingId={dogId}
                                status={dog.status}
                            />
                        </DogMobileHero>
                    )}
                </>
            ))()}

            {(() => (
                <>
                    {adoptedSummary ? (
                        <AdoptedDogSummary
                            dogId={dogId}
                            name={dogName}
                            breed={breedNamesDisplay}
                            gallery={gallery}
                            onOpenPhoto={openPhoto}
                            actionRow={
                                <DogActionRow
                                    action={action}
                                    inlineBuy={inlineBuy}
                                    statusContact={statusContact}
                                    onSentinel={setActionRowSentinel}
                                    onRow={setActionRow}
                                    contactHelp={wrapContact("mobile")}
                                    className="mt-4 md:mt-6"
                                />
                            }
                            actions={saveShareActions}
                            headerHandoff={headerHandoffSentinel}
                            provider={
                                organizationId && breederName
                                    ? {
                                          id: organizationId,
                                          name: breederName,
                                      }
                                    : null
                            }
                            recommendations={
                                <ListingRecommendations
                                    mode={mode}
                                    dog={dog}
                                    userLocation={userLocation}
                                    className="max-w-none px-0 py-0 pb-0 md:py-0 md:pb-0"
                                />
                            }
                        />
                    ) : (
                        <div
                            ref={contentSheetRef}
                            className={cn(
                                "w-full px-6 flex flex-col relative z-10 -mt-[var(--radius-surface)] overflow-hidden rounded-t-surface bg-true-white pt-7 pb-4 dog-desktop:mt-0 dog-desktop:overflow-visible dog-desktop:rounded-none dog-desktop:bg-transparent dog-desktop:pt-0 dog-desktop:pb-0",
                                DOG_DESKTOP_GRID,
                            )}
                        >
                            {(() => {
                                const showContactDetails =
                                    action.kind === "buy" && action.contactable;
                                const summaryBreed =
                                    presentation.showListingDetails
                                        ? undefined
                                        : breedNamesDisplay;
                                return (
                                    <div
                                        className={`order-1 min-w-0 dog-desktop:row-start-1 ${DOG_CONTENT_COLUMN} dog-desktop:pt-[calc(var(--dog-header-height)+1.5rem)]`}
                                    >
                                        {DOG_PAGE_LABEL[mode]}
                                        {buyBoxPrice === null && (
                                            <div className="hidden dog-desktop:block">
                                                <UnpricedListingSummary
                                                    name={dogName}
                                                    providerType={providerType}
                                                    breed={summaryBreed}
                                                    providerName={
                                                        buyBoxVariant === "bare"
                                                            ? undefined
                                                            : breederName
                                                    }
                                                    providerId={
                                                        dog.organizationId ??
                                                        undefined
                                                    }
                                                    providerIsVerified={
                                                        providerIsVerified
                                                    }
                                                    actions={saveShareActions}
                                                    primaryAction={
                                                        showContactDetails
                                                            ? primaryAction
                                                            : undefined
                                                    }
                                                    contactHelp={wrapContact(
                                                        "desktop",
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <div className="text-left dog-desktop:hidden">
                                            {showContactDetails &&
                                                buyBoxPrice !== null && (
                                                    <p className="mb-2 text-price text-doberman-black/65">
                                                        {buyBoxPrice}
                                                    </p>
                                                )}
                                            <h1 className="min-w-0 text-page-title text-doberman-black text-balance break-words">
                                                {dogName}
                                            </h1>
                                            {summaryBreed && (
                                                <p className="mt-2 text-body-small text-doberman-black/70">
                                                    {summaryBreed}
                                                </p>
                                            )}
                                            {showContactDetails &&
                                                breederName && (
                                                    <div className="mt-1 break-words">
                                                        <ListingProviderName
                                                            name={breederName}
                                                            providerType={
                                                                providerType
                                                            }
                                                            id={
                                                                dog.organizationId ??
                                                                undefined
                                                            }
                                                            isVerified={
                                                                providerIsVerified
                                                            }
                                                        />
                                                    </div>
                                                )}
                                        </div>
                                        <DogActionRow
                                            action={action}
                                            inlineBuy={inlineBuy}
                                            statusContact={statusContact}
                                            onSentinel={setActionRowSentinel}
                                            onRow={setActionRow}
                                            contactHelp={wrapContact("mobile")}
                                        />
                                        {!vitalsListing && kitsPillHold}
                                        {moreDogs.otherDogsSection}
                                        {buyBoxPrice !== null && (
                                            <div className="mt-2 hidden dog-desktop:block">
                                                <DesktopBuyBox
                                                    variant={buyBoxVariant}
                                                    providerType={providerType}
                                                    name={dogName}
                                                    priceDisplay={buyBoxPrice}
                                                    providerName={breederName}
                                                    providerId={
                                                        dog.organizationId ??
                                                        undefined
                                                    }
                                                    providerIsVerified={
                                                        providerIsVerified
                                                    }
                                                    contact={
                                                        showContactDetails
                                                            ? primaryAction
                                                            : undefined
                                                    }
                                                    actions={saveShareActions}
                                                    contactHelp={wrapContact(
                                                        "desktop",
                                                    )}
                                                />
                                            </div>
                                        )}
                                        {headerHandoffSentinel}
                                        {presentation.showListingDetails &&
                                            vitalsListing && (
                                                <div className="mt-4 dog-desktop:mt-8">
                                                    {!hideVitals && (
                                                        <VitalsSection
                                                            data={vitalsListing}
                                                        />
                                                    )}
                                                    {kitsPillHold}
                                                </div>
                                            )}
                                    </div>
                                );
                            })()}

                            {showDesktopPhoto ? (
                                <div className="hidden min-w-0 dog-desktop:block dog-desktop:sticky dog-desktop:top-header-inset dog-desktop:col-span-7 dog-desktop:col-start-1 dog-desktop:row-start-1 dog-desktop:row-span-2 dog-desktop:mt-4 dog-desktop:h-below-header-inset dog-desktop:self-start dog-desktop:overflow-hidden dog-desktop:rounded-card dog-desktop:mr-4">
                                    {desktopPhotoWell(
                                        dogId,
                                        gallery,
                                        openPhoto,
                                    )}
                                    {heroSticker && (
                                        <StatusSticker
                                            id={dogId}
                                            status={heroSticker}
                                            size="hero"
                                            className="absolute top-6 left-6 z-10"
                                        />
                                    )}
                                </div>
                            ) : null}

                            {(() => (
                                <>
                                    <div
                                        className={cn(
                                            `order-3 mt-8 min-w-0 flex flex-col dog-desktop:row-start-2 ${DOG_CONTENT_COLUMN} dog-desktop:mt-8 dog-desktop:pb-16`,
                                            !presentation.showListingDetails &&
                                                "pb-[calc(env(safe-area-inset-bottom)+6rem)]",
                                        )}
                                    >
                                        <div className="flex flex-col">
                                            <h2 className="text-section-title text-doberman-black text-balance">
                                                A little about them
                                            </h2>
                                            <ReadMoreExpandable
                                                key={dog.id}
                                                renderPhoneLink={
                                                    mode === "listing"
                                                        ? (linkProps) => (
                                                              <Link
                                                                  to="/dog/$dogId/about"
                                                                  params={{
                                                                      dogId: dog.id,
                                                                  }}
                                                                  {...linkProps}
                                                              />
                                                          )
                                                        : undefined
                                                }
                                                viewportFraction={0.58}
                                                contentClassName="mt-4 max-w-prose whitespace-pre-line break-words"
                                            >
                                                <ListingDescriptionMarkdown>
                                                    {dogDescription}
                                                </ListingDescriptionMarkdown>
                                            </ReadMoreExpandable>
                                        </div>

                                        {presentation.showListingDetails && (
                                            <DogListingDetails
                                                listing={listing}
                                                providerType={providerType}
                                                providerName={breederName}
                                                included={
                                                    listingPageV2?.application
                                                        ?.included
                                                }
                                                sectionProps={sectionProps}
                                                recommendations={
                                                    <ListingRecommendations
                                                        mode={mode}
                                                        dog={dog}
                                                        userLocation={
                                                            userLocation
                                                        }
                                                        className="max-w-none px-0"
                                                    />
                                                }
                                            />
                                        )}
                                    </div>
                                </>
                            ))()}
                        </div>
                    )}
                </>
            ))()}

            {(() => (
                <>
                    <FloatingListingAction
                        action={dock.floating}
                        primaryAction={primaryAction}
                        contact={statusContact}
                    />
                    {copilotEnabled && (
                        <KitsCopilotPageContext
                            listingId={dogId}
                            listingName={dogName}
                            hideClosedPill={adoptedSummary}
                            contactPreview={contactPreview}
                            compatibility={compatibilityInputFromWagsListing(
                                dog,
                            )}
                            transaction={transaction ?? undefined}
                            missingDetail={missingListingDetail(
                                listingState,
                                listingPageV2 && listing,
                            )}
                        />
                    )}
                    <ListingCarousel
                        mounted={carouselMounted}
                        mediaItems={mediaItems}
                        open={isPhotoCarouselOpen}
                        onOpenChange={setIsPhotoCarouselOpen}
                        alt={dogName}
                        index={selectedMediaIndex}
                        onIndexChange={setSelectedMediaIndex}
                    />
                </>
            ))()}
        </div>
    );
    return renderPage();
}
